/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalProps, LoadingSpinner, colors } from '@blend/consumer-components';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { css, StyleSheet } from 'aphrodite';

import { useRootSelector } from '../../../../hooks/useRootSelector';
import { getStyleConfig, styles as modalStyles } from '../styles';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { noop } from '@utils/lodash';

const styles = StyleSheet.create({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

export namespace CountDownSpinnerModal {
  export interface OwnProps extends ModalProps {
    heading: string;
    description: string;
    countDownSeconds: number;
    onTimerEnds: () => void;
  }
  export type Props = OwnProps & TimerProps;
}

export const _CountDownSpinnerModal: React.FC<CountDownSpinnerModal.Props> = ({ reactSafeSetInterval, ...props }) => {
  const isMobileView = useRootSelector(state => state.layout.isMobileView);
  const [time, setTime] = useState(props.countDownSeconds);
  const timerEl = useRef<any>(null);

  useEffect(() => {
    if (time === 0) {
      props.onTimerEnds();
    }
  }, [time]);

  useEffect(() => {
    if (props.isOpen) {
      setTime(() => props.countDownSeconds);
      timerEl.current = reactSafeSetInterval(() => {
        setTime(prevTime => prevTime - 1);
      }, 1000);
      return () => {
        window.clearInterval(timerEl.current);
      };
    }

    return noop;
  }, [props.isOpen]);

  const timerEnded = time <= 0;

  return (
    <Modal styleConfig={getStyleConfig(isMobileView)} {...props} data-it="CountDownSpinnerModal">
      <div className={css(modalStyles.content, styles.content)}>
        <div className={css(modalStyles.heading)}>{props.heading}</div>
        {!timerEnded && <LoadingSpinner size="sm" color={colors.catalyst} />}
        <div className={css(modalStyles.description)} data-it="CountDownSpinnerModal-Description">
          {props.description}
        </div>
        {!timerEnded && (
          <p className={css(modalStyles.description)} data-it="CountDownSpinnerModal-TimeLeft">
            Please wait <strong>{time} seconds</strong>
          </p>
        )}
        {timerEnded && (
          <ActionButton data-dd-privacy="allow" onClick={props.handleClose} data-it="ActionButton-Close">
            Close
          </ActionButton>
        )}
      </div>
    </Modal>
  );
};

export const CountDownSpinnerModal = (props: CountDownSpinnerModal.OwnProps) => (
  <ProvideTimers render={timerProps => <_CountDownSpinnerModal {...props} {...timerProps} />} />
);
